import {
  Button,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from '@mui/material'
import moment from 'moment'
import React from 'react'
import { useFormContext } from 'react-hook-form'

import Icon from 'components/Icon'
import { ViewName } from 'components/services/create/CreateServiceStepper'
import { TargetingSection } from 'components/services/details/TargetingSection'
import theme from 'theme'
import { TargetingStrategy } from 'types/api/attacks/details/targeting'
import { ServiceFormValues } from 'types/forms'
import { getWeekDay } from 'utils/days'

const ServiceSummarySection: React.FC<{
  name: string
  onEdit: (num) => void
  rows: Record<string, string>[] | Record<string, TargetingStrategy>[]
}> = ({ name, onEdit, rows }) => {
  return (
    <>
      <Stack
        data-test-service-summary-section-header-group={name}
        direction="row"
        spacing={2}
      >
        <Typography
          data-test-service-summary-section-header={name}
          variant="h6"
        >
          {name}
        </Typography>
        <Button
          data-test-service-summary-section-edit-button={name}
          startIcon={<Icon icon="edit" />}
          variant="outlined"
          onClick={onEdit}
        >
          Edit
        </Button>
      </Stack>
      <Table aria-label="Service Summary Table">
        <TableBody>
          {rows?.map((row, index) => (
            <TableRow
              key={index}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              {row.targetingStrategy ? (
                <TableCell>
                  <TargetingSection
                    data-test-service-summary-section-table-row={
                      row.targetingStrategy
                    }
                    targetingStrategy={row.targetingStrategy}
                  />
                </TableCell>
              ) : (
                <>
                  {Boolean(row.name) && (
                    <TableCell
                      data-test-service-summary-section-table-row={row.name}
                    >
                      {row.name}
                    </TableCell>
                  )}
                  {Boolean(row.value) && (
                    <TableCell
                      css={{ overflowWrap: 'anywhere' }}
                      data-test-service-summary-section-table-row={row.value}
                    >
                      {row.value}
                    </TableCell>
                  )}
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  )
}

export const ServiceSummary: React.FC<{
  onEdit: (num) => void
  steps: string[]
}> = ({ onEdit, steps }) => {
  const { getValues } = useFormContext<ServiceFormValues>()

  const {
    name,
    type,
    processName,
    statusCheckNames,
    targetingStrategy,
    scheduleSettings
  } = getValues()

  const { enabled, triggerType, activeDays, start, end } = scheduleSettings

  const ServiceSummaryTableValues = React.useMemo(() => {
    let autoSchedulingDetails = [{ name: 'Enabled', value: enabled.toString() }]

    // Only show Autoscheduling details if it is enabled
    if (enabled) {
      const startMoment = moment.utc(start)
      const endMoment = moment.utc(end)

      autoSchedulingDetails = autoSchedulingDetails.concat([
        { name: 'Trigger Type', value: triggerType },
        { name: 'Scheduled Day', value: getWeekDay(activeDays[0]) },
        {
          name: 'Test Window Start Time',
          value: `${startMoment.format('hh:mm A')}/${startMoment
            .parseZone()
            .zoneAbbr()}`
        },
        {
          name: 'Test Window End Time',
          value: `${endMoment.utc().format('hh:mm A')}/${endMoment
            .parseZone()
            .zoneAbbr()}`
        }
      ])
    }

    return {
      [ViewName.ServiceType]: [
        { name: 'Service Name', value: name },
        {
          name: 'Type of Service',
          value: type
        }
      ],
      [ViewName.ServiceFingerprint]: [{ targetingStrategy }],
      [ViewName.Process]: [{ name: 'Process Name', value: processName }],
      [ViewName.GoldenSignals]: statusCheckNames.map((statusCheckName) => ({
        name: 'Golden Signal Name',
        value: statusCheckName
      })),
      [ViewName.AutoScheduling]: autoSchedulingDetails
    }
  }, [
    enabled,
    name,
    type,
    targetingStrategy,
    processName,
    statusCheckNames,
    start,
    end,
    triggerType,
    activeDays
  ])

  return (
    <>
      <Typography
        css={{
          paddingBottom: theme.space[4]
        }}
        variant="body1"
      >
        Review your Service and make any changes needed. Once Gremlin creates
        your Service, you can run Reliability Tests on it to get a Reliability
        Score.
      </Typography>

      <Grid container flexDirection="column" spacing={2}>
        {steps.map((step, index) => {
          if (step !== 'Summary') {
            return (
              <Grid
                key={`service-summary-section-${step}-${index}`}
                item
                data-test-service-summary-section={`${step}-${index}`}
                xs={6}
              >
                <ServiceSummarySection
                  name={step}
                  rows={ServiceSummaryTableValues[step]}
                  onEdit={() => onEdit(index)}
                />
              </Grid>
            )
          }
        })}
      </Grid>
    </>
  )
}
